// Bid Form tab — printable proposal preview (Letter page) with the cost waterfall.
import { useContext } from "react";
import { Building2, Printer, SlidersHorizontal } from "lucide-react";
import { AuthContext } from "context/AuthContext";
import TabSkeleton from "../TabSkeleton";
import { useWorkspace } from "../../../../hooks/useEstimateWorkspace";
import { BRAND_MODES } from "../../../../mock/config.mock";
import { Button, Card, Field, NumberInput, Segmented, TextArea, YES_NO } from "../../../../components/ui";
import { fmtCurrency, fmtDate, fmtDec2, fmtHrs, fmtQty } from "../../../../utils/format";
import { WaterfallChart, WaterfallTable } from "./CostWaterfall";

// Print only the proposal page: everything else on screen is hidden for @media print.
const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #bid-proposal, #bid-proposal * { visibility: visible !important; }
  #bid-proposal { position: absolute; left: 0; top: 0; width: 100%; margin: 0; border: 0; box-shadow: none; }
  @page { size: letter; margin: 0.5in; }
}`;

function InfoItem({ label, value }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10.5px] font-semibold uppercase tracking-wide text-text_muted">{label}</dt>
      <dd className="truncate text-[12.5px] text-text_primary">{value || "—"}</dd>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-[4px] border border-border_soft bg-surface_alt px-3 py-2">
      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-text_muted">{label}</div>
      <div className="text-[14px] font-semibold tabular-nums text-text_primary">{value}</div>
    </div>
  );
}

function ScopeText({ label, value, onChange, disabled }) {
  return (
    <div>
      <h4 className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-text_secondary">{label}</h4>
      <TextArea rows={5} value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} aria-label={label} className="print:hidden" />
      <p className="hidden whitespace-pre-line text-[12px] text-text_primary print:block">{value}</p>
    </div>
  );
}

function SignatureLine({ label, name }) {
  return (
    <div>
      <div className="h-10 border-b border-border_strong" />
      <div className="mt-1 flex justify-between text-[11px] text-text_muted">
        <span>
          {label}
          {name ? ` — ${name}` : ""}
        </span>
        <span>Date: ____________</span>
      </div>
    </div>
  );
}

export default function BidFormTab() {
  const { user } = useContext(AuthContext);
  const { calc, draft, estimate, config, isLocked, setProject, setText } = useWorkspace();
  if (!calc || !draft || !estimate) return <TabSkeleton cards={3} />;

  const p = draft.project;
  const { waterfall, kpis } = calc;
  const companyName = user?.company?.company_name || "Surya Group";
  const brandLabel = BRAND_MODES.find((b) => b.value === p.brandMode)?.label;

  return (
    <div className="space-y-4">
      <style>{PRINT_CSS}</style>

      <Card className="flex flex-wrap items-end gap-4 px-4 py-3 print:hidden" aria-label="Bid controls">
        <span className="inline-flex items-center gap-2 self-center text-[12.5px] font-semibold text-text_primary">
          <SlidersHorizontal size={15} className="text-primary" aria-hidden="true" /> Bid controls
        </span>
        <Field label="Bond required" className="w-auto">
          <Segmented options={YES_NO} value={!!p.bondRequired} onChange={(v) => setProject({ bondRequired: v })} ariaLabel="Bond required" disabled={isLocked} />
        </Field>
        <Field label="Contingency %" htmlFor="bid-contingency" className="w-28">
          <NumberInput
            id="bid-contingency"
            min={0}
            max={25}
            step={0.5}
            value={p.contingencyPct === "" ? "" : +(Number(p.contingencyPct || 0) * 100).toFixed(2)}
            onChange={(v) => setProject({ contingencyPct: v === "" ? 0 : Math.max(0, v) / 100 })}
            disabled={isLocked}
          />
        </Field>
        <Field label="Project insurance ($)" htmlFor="bid-insurance" className="w-40">
          <NumberInput
            id="bid-insurance"
            min={0}
            step={50}
            value={p.projectInsurance}
            onChange={(v) => setProject({ projectInsurance: v === "" ? 0 : Math.max(0, v) })}
            disabled={isLocked}
          />
        </Field>
        <div className="ml-auto">
          <Button variant="primary" icon={Printer} onClick={() => window.print()}>
            Print / Save PDF
          </Button>
        </div>
      </Card>

      <article
        id="bid-proposal"
        className="mx-auto w-full max-w-[8.5in] rounded-[4px] border border-border bg-surface px-8 py-8 shadow-sm print:px-0 print:py-0"
        aria-label="Bid proposal preview"
      >
        <header className="flex flex-wrap items-start justify-between gap-4 border-b-2 border-primary pb-4">
          <div className="flex items-center gap-3">
            {user?.company?.company_url ? (
              <img src={user.company.company_url} alt={`${companyName} logo`} className="h-12 w-12 rounded-[4px] object-cover" />
            ) : (
              <span className="flex h-12 w-12 items-center justify-center rounded-[4px] bg-primary text-white">
                <Building2 size={24} aria-hidden="true" />
              </span>
            )}
            <div>
              <div className="text-[17px] font-bold text-text_primary">{companyName}</div>
              <div className="text-[11.5px] text-text_muted">Commercial Painting & Coatings</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[15px] font-bold uppercase tracking-wide text-secondary">Painting Proposal</div>
            <div className="text-[11.5px] text-text_secondary">Bid # {p.bidNumber || "—"}</div>
            <div className="text-[11.5px] text-text_secondary">Date {fmtDate(p.bidDate)}</div>
          </div>
        </header>

        <section className="py-4">
          <h2 className="text-[15px] font-semibold text-text_primary">{p.projectName}</h2>
          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2.5 sm:grid-cols-3">
            <InfoItem label="Location" value={[p.location, p.state].filter(Boolean).join(", ")} />
            <InfoItem label="Client / GC" value={p.clientGc} />
            <InfoItem label="Estimator" value={p.estimator} />
            <InfoItem label="Job" value={`${estimate.jobId} – ${estimate.jobTitle}`} />
            <InfoItem label="Estimate" value={`${estimate.id} · ${estimate.status === "FINALIZED" ? "Finalized" : "Draft"}`} />
            <InfoItem label="Settings" value={config?.versionLabel} />
            <InfoItem label="Brand mode" value={brandLabel} />
            <InfoItem label="Condition" value={p.conditionDefault} />
            <InfoItem label="Tax / Bond" value={`${p.taxExempt ? "Tax exempt" : "Taxable"} · ${p.bondRequired ? "Bond required" : "No bond"}`} />
          </dl>
        </section>

        <section className="rounded-[4px] border border-border" aria-label="Cost waterfall">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <h3 className="text-[13px] font-semibold text-text_primary">Cost waterfall</h3>
            <span className="text-[13px] font-bold tabular-nums text-secondary">{fmtCurrency(waterfall.total)}</span>
          </div>
          <div className="px-2 pt-3">
            <WaterfallChart steps={waterfall.steps} />
          </div>
          <WaterfallTable steps={waterfall.steps} />
        </section>

        <section className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-5" aria-label="Bid metrics">
          <Metric label="Total paintable SF" value={fmtQty(kpis.paintableSf)} />
          <Metric label="Price / SF" value={fmtCurrency(kpis.pricePerSf)} />
          <Metric label="Total labor hrs" value={fmtHrs(kpis.laborHours)} />
          <Metric label="Crew man-days" value={fmtQty(kpis.manDays)} />
          <Metric label="Difficulty factor" value={`×${fmtDec2(p.difficultyFactor)}`} />
        </section>

        <section className="mt-5 flex flex-col items-end gap-0.5 border-y border-border py-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text_muted">Total bid price</span>
          <span className="text-[22px] font-bold tabular-nums text-secondary">{fmtCurrency(waterfall.total)}</span>
        </section>

        <section className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ScopeText label="Inclusions" value={draft.inclusions} onChange={(v) => setText("inclusions", v)} disabled={isLocked} />
          <ScopeText label="Exclusions" value={draft.exclusions} onChange={(v) => setText("exclusions", v)} disabled={isLocked} />
        </section>

        <p className="mt-4 text-[11px] text-text_muted">
          This proposal is valid for 30 days from the bid date. Pricing is based on the takeoff and finish schedule provided; changes in scope
          will be priced separately.
        </p>

        <section className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2" aria-label="Signatures">
          <SignatureLine label={`Submitted by ${companyName}`} name={p.estimator} />
          <SignatureLine label="Accepted by Client / GC" name={p.clientGc} />
        </section>
      </article>
    </div>
  );
}
